import type React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { useAppState } from 'state';

import type {
  Equipment,
  SaveLessonByIdMutation,
  SaveLessonByIdMutationVariables,
  UnsaveLessonByIdMutation,
  UnsaveLessonByIdMutationVariables,
} from 'app/types/graphql';

import Button from 'app/ui/components/atoms/button';
import ClassCard from 'app/ui/components/atoms/class-card';
import Typography from 'app/ui/components/atoms/typography';
import {
  SaveLessonByIdClassCard as SAVE_LESSON,
  UnsaveLessonByIdClassCard as UNSAVE_LESSON,
} from 'app/ui/components/atoms/class-card/mutations.gql';

import Loading from 'app/ui/components/molecules/loading';
import EmptyState from 'app/ui/components/molecules/empty-state';
import { type Pills, PillFieldSet } from 'app/ui/components/molecules/pill-field-set';

import GhostIcon from 'app/ui/components/atoms/ghost';
import FiltersIcon from 'app/ui/components/atoms/icons/filters';
import { routes } from 'app/routes/constants';

const WorkoutListContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => `
    gap: ${spacing(8)};
  `}
`;

const WorkoutsTitleWrapper = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;

  justify-content: space-between;
  align-items: flex-start;
  flex-direction: column;
  gap: 2rem;

  span[class*='IconWrapper'] {
    display: inline-flex;
  }

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        gap: 0;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
      `,
    )}
  `}
`;

const WorkoutsTiles = styled.div`
  width: 100%;
  display: grid;
  align-self: center;

  > div[class*='card'] {
    width: 100%;
  }
  
  ${({ theme: { spacing } }) => `
    gap: ${spacing(5)};
  `}

  ${({ theme: { media } }) => `
    ${media.small(
      () => `
        grid-template-columns: 1fr; // 1 item per row for mobile
      `,
    )}

    ${media.medium(
      () => `
        grid-template-columns: repeat(2, 1fr); // 2 items per row for tablets
      `,
    )} 


    ${media.large(
      () => `
        grid-template-columns: repeat(3, 1fr); // 3 items per row for large screens
      `,
    )}
  `}
`;

const WorkoutsFilter = styled.div`
  display: flex;
  margin-top: 0;
  margin-bottom: 0;

  div[class*='Fieldset'] {
    overflow-x: auto;
    flex-wrap: nowrap;
    margin: 0;

    -ms-overflow-style: none;
    scrollbar-width: none;

    ${({ theme: { media } }) => `
      ${media.medium(
        () => `
          overflow-x: none;
          flex-wrap: wrap;
        `,
      )}
    `}
  }
`;

const StyledButton = styled(Button)`
  align-self: stretch;

  ${({ theme: { media } }) => `
      ${media.medium(
        () => `
          align-self: unset;
        `,
      )}
    `}
`;

export type Workout = {
  id: string;
  srcSet: string;
  title: string;
  intensity: string;
  equipment?: Equipment[] | null;
  isSaved: boolean;
  isCompleted: boolean;
  label?: string;
  isDisabled?: boolean;
  isDone?: boolean;
};

type WorkoutListWithDataProps = {
  workouts: Workout[];
  title?: string;
  largeTitle?: boolean;
  allowMoreFilters?: boolean;
  conceptFilters?: Pills;
  handleConceptChange?: (value: string) => void;
  setShowMoreFilters?: (value: boolean) => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
};

const WorkoutList: React.FC<WorkoutListWithDataProps> = ({
  title,
  largeTitle,
  isLoading,
  isLoadingMore,
  workouts,
  allowMoreFilters = false,
  conceptFilters,
  handleConceptChange,
  setShowMoreFilters,
}) => {
  const intl = useIntl();
  const userId = useAppState(({ auth }) => auth.userId);

  const [saveLesson] = useMutation<SaveLessonByIdMutation, SaveLessonByIdMutationVariables>(SAVE_LESSON);
  const [unsaveLesson] = useMutation<UnsaveLessonByIdMutation, UnsaveLessonByIdMutationVariables>(UNSAVE_LESSON);

  const handleSaveLesson = async (lessonId: string): Promise<{ isSaved: boolean }> => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    const result = await saveLesson({
      variables: { id: lessonId, input: { user: { id: userId } } },
    });
    return { isSaved: result.data?.saveLessonById.isSaved ?? false };
  };

  const handleUnsaveLesson = async (lessonId: string): Promise<{ isSaved: boolean }> => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    const result = await unsaveLesson({
      variables: { id: lessonId, input: { user: { id: userId } } },
    });
    return { isSaved: result.data?.unsaveLessonById.isSaved ?? false };
  };

  const showInLineFilters = conceptFilters && conceptFilters.length > 0 && handleConceptChange;

  const filterBtnText = intl.formatMessage({
    id: showInLineFilters ? 'workoutLibrary.buttons.filters' : 'workoutLibrary.browse',
  });

  const emptyStateTitle = intl.formatMessage({ id: 'filters.emptyState.title' });
  const emptyStateCopy = intl.formatMessage({ id: 'filters.emptyState.copy' });

  const showEmptyState = !isLoading && workouts.length === 0;

  return (
    <WorkoutListContainer>
      <WorkoutsTitleWrapper>
        {title && <Typography variant={largeTitle ? 'h3' : 'h4'}>{title}</Typography>}
        {allowMoreFilters && setShowMoreFilters && (
          <StyledButton
            type="secondary-white"
            label={filterBtnText}
            icon={<FiltersIcon color="white" />}
            iconPosition="left"
            onClick={() => setShowMoreFilters(true)}
          />
        )}
      </WorkoutsTitleWrapper>

      {showInLineFilters && (
        <WorkoutsFilter>
          <PillFieldSet label="" pills={conceptFilters} onChange={(event) => handleConceptChange(event.target.value)} />
        </WorkoutsFilter>
      )}

      {isLoading && <Loading />}

      {showEmptyState && <EmptyState icon={<GhostIcon />} heading={emptyStateTitle} subheading={emptyStateCopy} />}

      {!isLoading && workouts.length > 0 && (
        <WorkoutsTiles>
          {workouts.map((workout, index) => (
            <ClassCard
              lessonId={workout.id}
              key={`${workout.title}-${index}`}
              srcSet={workout.srcSet}
              title={workout.title}
              // TODO: navigate tidy
              to={routes.WORKOUT.replace(':id', workout.id)}
              intensity={workout.intensity}
              equipment={workout.equipment}
              isSaved={workout.isSaved}
              isCompleted={workout.isCompleted}
              testId="workout-list-class-card"
              onSaveLesson={handleSaveLesson}
              onUnsaveLesson={handleUnsaveLesson}
            />
          ))}
        </WorkoutsTiles>
      )}
      {isLoadingMore && <Loading />}
    </WorkoutListContainer>
  );
};

export default WorkoutList;
